import * as React from 'react';
import {createBottomTabNavigator} from '@react-navigation/bottom-tabs';
import Home from '../screens/home/Home';
import {
  fantasyTab,
  fantasyTab_selected,
  learnTab,
  liveTab,
  liveTab_selected,
  marketTab,
  marketTab_selected,
  portfolioTab,
  portfolioTab_selected,
  walletTab,
  walletTab_selected,
  activityTab,
  activityTab_selected,
  more,
  more_selected,
} from '../utils/images';
import {Dimensions, Platform, StyleSheet, View, Text, Animated} from 'react-native';
import Colors from '../assets/Color/Colors';
import {globalFontStyle} from '../assets';
import Wallet from '../screens/wallet/wallet';
import Account from '../screens/account/account';
import Login from '../screens/Login/Login';
import Portfolio from '../screens/portfolio/Portfolio';
import {useSelector, useDispatch} from 'react-redux';
import Community from '../commonComponents/Community';
import {useSafeAreaInsets} from 'react-native-safe-area-context';
import {Image} from 'expo-image';
import FastImage from '../common/FastImage';
import Width from '../assets/Width';
import Contest from '../screens/contest/contest';
import {getLoginRoute, isMobile, showLoginModal} from '../utils/commonMethods';
import {globalImport} from '../common/globalImports';
import {convert} from '../locale/translateText';
import {
  BottomTab,
  globalConfigurableData,
  partnerMap,
} from '../utils/globalConstant';
import TransactionScreen from '../screens/wallet/childScreens/TransactionScreen';
import Transaction from '../screens/wallet/childScreens/Transaction';
import RewardsMain from '../screens/contest/RewardsMain';
import HowToTrade from '../screens/home/childComponents/howToTrade';
import Activity from '../screens/activity/Activity';
import { Icon } from '@iconify/react';

const Tab = createBottomTabNavigator();

const iconConfig = {
  market: marketTab(),
  wallet: walletTab(),
  live: liveTab(),
  community: learnTab(),
  portfolio: portfolioTab(),
  contest: fantasyTab(),
  activity: activityTab(),
  more: "tabler:dots-vertical",
};

const iconSelectedConfig = {
  market: marketTab_selected(),
  wallet: walletTab_selected(),
  live: liveTab_selected(),
  portfolio: portfolioTab_selected(),
  contest: fantasyTab_selected(),
  activity: activityTab_selected(),
  more: "tabler:dots-vertical",
};

export const HomeNavigator = ({storeData, navigation}) => {
  const dispatch = useDispatch();
  const isAppLogin = useSelector(({utilReducer}) => utilReducer.isAppLogin);
  const globalConfigData = useSelector(
    state => state.utilReducer.globalConfigData,
  );
  const isContestVisible = useSelector(
    state => state.contestLeaderboardReducer.isContestVisible,
  );
  const userInfo = useSelector(({profile}) => profile.profileData);
  const portfolioData = useSelector(({portfolioReducer}) => portfolioReducer.data1);
  const liveData = useSelector(({liveReducer}) => liveReducer.byIds);

  const insets = useSafeAreaInsets();

  const getTotalWalletBalance = () => {
    const depositBalance = userInfo?.coinsd >= 0 ? Math.floor(userInfo?.coinsd) : 0;
    const winningsBalance = userInfo?.coinsw >= 0 ? Math.floor(userInfo?.coinsw) : 0;
    const bonusBalance = userInfo?.coinsb >= 0 ? Math.floor(userInfo?.coinsb) : 0;
    return depositBalance + winningsBalance + bonusBalance;
  };

  const getCurrentInvestedAmount = () => {
    if (!portfolioData) return "--";

    let openOrderValue = 0;
    
    // Get first portfolio key since that's what we need
    const portfolioKey = Object.keys(portfolioData)[0];
    if (!portfolioKey) return "--";

    const portfolio = portfolioData[portfolioKey];
    if (!portfolio) return "--";

    const calls = portfolio?.calls || [];

    calls.forEach(call => {
      if (call.rank === -1 && call.status === "A") {
        openOrderValue += call.noofcontracts * call.coins;
      } else if (call.rank === -1 && call.status === "H") {
        openOrderValue += call.noofcontracts * call.lastprice;
      }
    });

    return openOrderValue.toFixed(2);
  };

  const renderTabIcon = ({focused}, screen) => {
    const color = focused ? BottomTab.tabSelectedColor : BottomTab.tabUnselectedColor;
    
    // Special handling for wallet tab
    if (screen === 'wallet') {
      if (isAppLogin) {
        return (
          <View style={[styles.tabIconContainer]}>
            <Text style={[
              globalFontStyle.appFontBody,
              {color},
              styles.walletText
            ]}>
              {getTotalWalletBalance()}
            </Text>
          </View>
        );
      } else {
        // Show wallet icon when logged out
        return (
          <View style={[styles.tabIconContainer]}>
            <Icon 
              icon={BottomTab.iconConfig.wallet}
              color={color}
              width="24"
              height="24"
            />
          </View>
        );
      }
    }

    // For all other tabs
    return (
      <View style={[styles.tabIconContainer]}>
        <Icon 
          icon={BottomTab.iconConfig[screen]}
          color={color}
          width="24"
          height="24"
        />
      </View>
    );
  };

  const handleAuthenticatedRoute = (e) => {
    if (!isAppLogin) {
      e.preventDefault();
      if (isMobile()) {
        showLoginModal(dispatch);
      } else {
        navigation.navigate('Login');
      }
    }
  };

  return (
    <Tab.Navigator
      screenOptions={{
        tabBarLabelStyle: {
          ...globalFontStyle.appFontBody, 
          fontSize: 11,
          marginTop: 4,
          fontWeight: '500'
        },
        headerShown: false,
        tabBarActiveTintColor: BottomTab.tabSelectedColor,
        tabBarInactiveTintColor: BottomTab.tabUnselectedColor,
        tabBarStyle: {
          elevation: 8,
          shadowColor: 'rgba(0, 0, 0, 0.1)',
          shadowOffset: {
            width: 0,
            height: -4,
          },
          shadowOpacity: 0.5,
          shadowRadius: 4,
          justifyContent: 'center',
          height: Platform.OS !== 'android' ? 60 + insets.bottom : 65,
          backgroundColor: BottomTab.tabBottomBarColor,
          borderTopWidth: 0,
        },
        tabBarItemStyle: {
          paddingVertical: 6,
          height: '100%',
        },
      }}
      sceneContainerStyle={{
        backgroundColor: globalConfigurableData.globalThemeColor,
      }}>
      <Tab.Screen
        name="Markets"
        component={Home}
        options={{
          tabBarLabel: convert(BottomTab.tabTextData[0]?.tabLabel),
          tabBarIcon: navProps => renderTabIcon(navProps, 'market'),
        }}
      />

      <Tab.Screen
        name="Portfolio"
        component={Portfolio}
        listeners={{
          tabPress: handleAuthenticatedRoute,
        }}
        options={{
          tabBarLabel: convert(BottomTab.tabTextData[1]?.tabLabel),
          tabBarIcon: navProps => renderTabIcon(navProps, 'portfolio'),
          unmountOnBlur: true,
        }}
      />

      <Tab.Screen
        name="Activity"
        component={Activity}
        listeners={{
          tabPress: handleAuthenticatedRoute,
        }}
        options={{
          tabBarLabel: convert(BottomTab.tabTextData[3]?.tabLabel),
          tabBarIcon: navProps => renderTabIcon(navProps, 'activity'),
          unmountOnBlur: true,
        }}
      />

      {BottomTab.tabTextData[4]?.isVisible && (
        <Tab.Screen
          name="Wallet"
          component={partnerMap?.isMyMaster ? Transaction : Wallet}
          listeners={{
            tabPress: handleAuthenticatedRoute,
          }}
          options={{
            tabBarLabel: convert(BottomTab.tabTextData[4]?.tabLabel),
            tabBarIcon: navProps => renderTabIcon(navProps, 'wallet'),
          }}
        />
      )}

      <Tab.Screen
        name="Rewards"
        component={RewardsMain}
        options={{
          tabBarButton: () => null,
          tabBarStyle: { display: 'none' }
        }}
      />

      <Tab.Screen
        name="More"
        component={Account}
        listeners={{
          tabPress: handleAuthenticatedRoute,
          state: ({data, type}) => {
            if (type === 'action' && data?.state?.routes) {
              const currentRoute = data.state.routes[data.state.index];
              const params = currentRoute?.params;
              if (params?.screen === 'rewards' || params?.redirect === 'rewards') {
                navigation.navigate('Rewards');
              }
            }
          },
        }}
        options={{
          tabBarLabel: 'More',
          tabBarIcon: navProps => renderTabIcon(navProps, 'more'),
          unmountOnBlur: true,
        }}
      />
    </Tab.Navigator>
  );
};

export const styles = StyleSheet.create({
  tabIconContainer: {
    justifyContent: 'center',
    alignItems: 'center',
    padding: 4,
  },
  walletText: {
    fontSize: 13,
    fontWeight: '600',
    marginBottom: 4,
  }
});
